import { ActionTitle } from "@/components/actionTitle/ActionTitle";
import { useModals } from "@/features/modals/hooks";
import styled from "@emotion/styled";
import { type Widget, type WidgetData } from "@repo/api";
import {
  Button,
  LightMode,
  Text,
  Variables,
  panIcon,
  trashCanIcon,
} from "@repo/skapa";
import { T, t } from "@repo/translation";
import { ButtonsContainer, ElementsContainer, translateLabel } from "@repo/ui";

const WidgetContainer = styled.div<{ isSelected?: boolean }>`
  padding: ${Variables.space100};
  height: 100%;
  background-color: ${LightMode.colourStaticWhite};
  border: ${Variables.thicknessThin} ${LightMode.colourNeutral7}
    ${({ isSelected }) => (isSelected ? "solid" : "dashed")};
  &:hover {
    border: ${Variables.thicknessThin} solid ${LightMode.colourNeutral7};
  }
  cursor: pointer;
  box-shadow: ${({ isSelected }) =>
    isSelected ? "0px 4px 16px rgba(0, 0, 0, 0.4)" : "none"};
  border-radius: ${Variables.radiusM};
`;

export const InstalledWidgetBox = ({
  widgetDefinition,
  widgetData,
  isSelected,
  isMoving,
  onClick,
  onSetMoving,
  onRemoveWidget,
}: {
  widgetDefinition: Widget;
  widgetData: WidgetData;
  isSelected?: boolean;
  isMoving: boolean;
  onClick: any;
  onSetMoving: (id: number | undefined) => void;
  onReorderWidget: (
    id: number,
    direction: "LEFT" | "RIGHT" | "UP" | "DOWN"
  ) => Promise<void>;
  onRemoveWidget: (id: number) => Promise<void>;
}) => {
  const { pushModal } = useModals();

  if (!widgetDefinition) {
    return null;
  }

  const name = widgetData.widgetAliasName ?? widgetDefinition.name;

  return (
    <WidgetContainer
      key={widgetData?.workspacePreferenceId ?? widgetDefinition.name}
      onClick={onClick}
      isSelected={isSelected}
    >
      <ElementsContainer>
        <ActionTitle
          title={`${translateLabel(name) ?? name} ${
            isSelected ? ` (${t("label.selected")})` : ""
          }`}
        />
        <ButtonsContainer justifyContent="space-between">
          <Button
            size="small"
            iconOnly
            type={isMoving ? "primary" : "secondary"}
            ssrIcon={panIcon}
            onClick={() => {
              onSetMoving(
                isMoving ? undefined : widgetData.workspacePreferenceId
              );
            }}
          />
          <Button
            size="small"
            iconOnly
            type="secondary"
            ssrIcon={trashCanIcon}
            onClick={() => {
              pushModal({
                type: "CONFIRM",

                onConfirm: () =>
                  onRemoveWidget(widgetData.workspacePreferenceId),
                title: t("action.remove_widget"),
                message: t("prompt.confirm_remove_widget"),
              });
            }}
          />
        </ButtonsContainer>
        {isMoving && (
          <Text>
            <T translationKey="prompt.widget_move_description" />
          </Text>
        )}
      </ElementsContainer>
    </WidgetContainer>
  );
};
